/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useLayoutEffect, useState, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';
import { useSuggestionCardPositioning } from '../../hooks/useSuggestionCardPositioning';
import { useSyncedSelectedSuggestion } from '../../hooks/useSyncedSelectedSuggestion';
import { addAcceptSuggestionAnalytics } from '../../utils/analytics';
import { updateSuggestionStatus } from '../../utils/api';
import { applySuggestion } from '../../utils/applySuggestion';

import { ScrollBackToSuggestionButton } from './ScrollBackToSuggestionButton';
import { SuggestionCard } from './SuggestionCard';

const styles = cssMap({
	targetAnchoring: {
		display: 'block',
		position: 'absolute',
		insetBlockStart: `anchor(start)`,
		insetInlineStart: `anchor(end)`,
	},
	cardContainer: {
		zIndex: 510, // same as akEditorFloatingDialogZIndex
		positionVisibility: 'anchors-valid',
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long', '0.3s'),
		transitionTimingFunction: token('motion.easing.inout.bold', 'ease-in-out'),
	},
	scrollBackContainer: {
		width: '280px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'fixed',
	},
	scrollBackTop: { top: token('space.300'), animation: token('motion.popup.enter.bottom') },
	scrollBackBottom: { bottom: token('space.300'), animation: token('motion.popup.enter.top') },
	// Longhand animation overrides must come AFTER the `animation` shorthand entries above
	// (scrollBackTop/scrollBackBottom) in the `css={[...]}` array so the @compiled
	// shorthand-property-sorting rule sees shorthand-before-longhand across all entries.
	scrollBackAnimation: {
		animationDelay: token('motion.duration.xxlong'),
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	scrollBackRight: {
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	pinnedBottomRight: {
		position: 'fixed',
		bottom: token('space.300'),
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	anchoredToTargetEnd: {
		position: 'absolute',
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	pinnedTop: {
		position: 'fixed',
		top: token('space.300'),
	},
	pinnedBottom: {
		position: 'fixed',
		bottom: token('space.300'),
	},
	anchoredToTarget: {
		position: 'absolute',
	},
	slideUp: {
		opacity: 0,
		transform: `translateY(calc(-100% - ${token('space.300')} - ${token('space.200')}))`,
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		transitionDelay: `${token('motion.duration.medium')}, ${token('motion.duration.xxlong')}`,
	},
	slideDown: {
		opacity: 0,
		transform: `translateY(calc(100% + ${token('space.300')} + ${token('space.200')}))`,
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		transitionDelay: `${token('motion.duration.medium')}, ${token('motion.duration.xxlong')}`,
	},
});

/**
 * We render this in a content component because rendering directly in a Decoration widget
 * will cause it to inherit font stylings from Editor which is not appropriate for this component.
 */
export const SuggestionCardContentComponent = ({
	api,
	onShowSidebar,
	view,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	onShowSidebar?: () => void;
	view?: EditorView;
}): JSX.Element | undefined => {
	const cardRef = useRef<HTMLDivElement>(null);
	const [isShowingOnlySuggestion, setIsShowingOnlySuggestion] = useState(false);
	/**
	 * We use useState & useCallback to set the ref so that the popup will mount correctly after the
	 * anchor mounts. We have both target + targetEnd (two widgets, one at top and one at bottom of a block)
	 * to detect when it becomes out of the Editor viewport.
	 */
	const [targetStart, setTargetStart] = useState<HTMLSpanElement>();
	const targetStartRef = useCallback((node: HTMLSpanElement) => {
		setTargetStart(node);
	}, []);
	const [targetEnd, setTargetEnd] = useState<HTMLSpanElement>();
	const targetEndRef = useCallback((node: HTMLSpanElement) => {
		setTargetEnd(node);
	}, []);
	const [targetBase, setTargetBase] = useState<HTMLSpanElement>();
	const targetBaseRef = useCallback((node: HTMLSpanElement) => {
		setTargetBase(node);
	}, []);
	const [root, setRoot] = useState<Element>();
	useLayoutEffect(() => {
		if (!view?.dom) {
			setRoot(undefined);
			return;
		}
		const _root = findOverflowScrollParent(view.dom);
		setRoot(_root || undefined);
	}, [view]);

	// Positioning-critical slice — kept synchronous (no debounce) so the CSS-anchor references
	// stay in lock-step with the ProseMirror transaction that swapped the selection.
	const { selectedSuggestion, baseLocalId } = useSyncedSelectedSuggestion(api);

	const firstToolCallLocalId =
		selectedSuggestion?.toolCalls[0] && 'localId' in selectedSuggestion.toolCalls[0].arguments
			? selectedSuggestion.toolCalls[0].arguments.localId
			: undefined;

	const { stackSize, orderedSuggestionIds, isHidden } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => {
			const {
				suggestionIdsByBaseLocalId,
				baseLocalIdBySuggestionId,
				selectedSuggestion: selectedId,
				orderedSuggestionIds,
				isHidden,
			} = state?.aiSuggestionsState ?? {};

			const _baseLocalId =
				baseLocalIdBySuggestionId && selectedId
					? baseLocalIdBySuggestionId.get(selectedId)
					: undefined;

			return {
				stackSize: _baseLocalId ? suggestionIdsByBaseLocalId?.get(_baseLocalId)?.size : undefined,
				orderedSuggestionIds,
				isHidden,
			};
		},
	);

	const {
		anchorLeft,
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
	} = useSuggestionCardPositioning({
		root,
		targetStart,
		targetEnd,
		targetBase,
		cardRef,
		id: baseLocalId,
	});

	const onToggleOnlySuggestion = useCallback(() => {
		const nextIsShowingOnlySuggestion = !isShowingOnlySuggestion;
		setIsShowingOnlySuggestion(nextIsShowingOnlySuggestion);

		if (!selectedSuggestion) {
			return;
		}

		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id, {
			hideDeletedDiffs: nextIsShowingOnlySuggestion,
		});
	}, [api, isShowingOnlySuggestion, selectedSuggestion]);

	const onScrollBackClick = useCallback(() => {
		if (!selectedSuggestion) {
			return;
		}
		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id);
	}, [api, selectedSuggestion]);

	if (selectedSuggestion === undefined || isHidden) {
		return;
	}

	const onClose = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: undefined,
			});
			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onDiscard = () => {
		api?.core?.actions.execute(({ tr }) => {
			updateSuggestionStatus({
				updates: [{ id: selectedSuggestion.id, status: 'DISMISSED' }],
			});

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});

			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onAccept = () => {
		updateSuggestionStatus({
			updates: [{ id: selectedSuggestion.id, status: 'ACTIONED' }],
		});

		api?.core?.actions.execute(({ tr }) => {
			addAcceptSuggestionAnalytics(api?.analytics?.actions)(tr, selectedSuggestion, 'card');
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});

		setTimeout(() => {
			applySuggestion({ api, suggestion: selectedSuggestion });
		}, 0);

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onNavigate = (direction: 'up' | 'down') => {
		const currentId = selectedSuggestion.id;
		if (!orderedSuggestionIds || orderedSuggestionIds?.length === 0) {
			return undefined;
		}
		const idx = orderedSuggestionIds.indexOf(currentId);
		if (idx === -1) {
			return undefined;
		}
		const delta = direction === 'up' ? -1 : 1;
		const nextIdx = (idx + delta + orderedSuggestionIds.length) % orderedSuggestionIds.length;
		const nextId = orderedSuggestionIds[nextIdx];
		// When only one suggestion exists, the modulo wraps back to the current id.
		// Skip navigation in that case so we don't re-select the suggestion that's about to be removed.
		if (nextId === currentId) {
			return undefined;
		}
		api?.aiSuggestions?.actions.selectSuggestion(nextId);
	};

	const shouldScrollUp = isTargetEndAboveViewport && isTargetAboveViewport;
	const shouldScrollDown = isTargetBelowViewport && isTargetEndBelowViewport;

	/**
	 * Mutually exclusive positioning modes for the card. Each represents a
	 * distinct combination of viewport state and resulting style:
	 *  - `isPinnedBottomRight`     : fixed to the bottom-right of the viewport
	 *  - `isAnchoredToTargetEnd`   : absolutely anchored to the end of the target
	 *  - `isPinnedTop`             : fixed to the top of the viewport
	 *  - `isPinnedBottom`          : fixed to the bottom of the viewport
	 *  - `isAnchoredToTarget`      : absolutely anchored to the target (default)
	 */
	const isPinnedBottomRight = isTargetRightOfViewport && isTargetEndBelowViewport;
	const isAnchoredToTargetEnd = isTargetRightOfViewport && !isTargetEndBelowViewport;
	const isPinnedTop = !isTargetRightOfViewport && isTargetAboveViewport;
	const isPinnedBottom =
		!isTargetRightOfViewport &&
		!isTargetAboveViewport &&
		(isCardBottomBelowViewport || isTargetBelowViewport);
	const isAnchoredToTarget =
		!isPinnedBottomRight && !isAnchoredToTargetEnd && !isPinnedTop && !isPinnedBottom;

	const isLeftPositionDynamic = isPinnedTop || isPinnedBottom;

	return (
		<>
			<span
				ref={targetEndRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: `--card-${firstToolCallLocalId}-end`,
				}}
			/>
			<span
				ref={targetStartRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: `--card-${firstToolCallLocalId}-start`,
				}}
			/>
			<span
				ref={targetBaseRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: `--button-${baseLocalId}`,
				}}
			/>
			{(shouldScrollUp || shouldScrollDown) && (
				<span
					css={[
						styles.cardContainer,
						styles.scrollBackContainer,
						isTargetRightOfViewport && styles.scrollBackRight,
						shouldScrollUp ? styles.scrollBackTop : styles.scrollBackBottom,
						styles.scrollBackAnimation,
					]}
					style={{
						left: !isTargetRightOfViewport && anchorLeft !== null ? anchorLeft : undefined,
					}}
				>
					<ScrollBackToSuggestionButton
						direction={shouldScrollUp ? 'up' : 'down'}
						onClick={onScrollBackClick}
					/>
				</span>
			)}
			<span
				css={[
					styles.cardContainer,
					isPinnedBottomRight && styles.pinnedBottomRight,
					isAnchoredToTargetEnd && styles.anchoredToTargetEnd,
					isPinnedTop && styles.pinnedTop,
					isPinnedBottom && styles.pinnedBottom,
					isAnchoredToTarget && styles.anchoredToTarget,
					shouldScrollUp && styles.slideUp,
					shouldScrollDown && styles.slideDown,
				]}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(isAnchoredToTarget && {
						insetBlockStart: `anchor(--card-${firstToolCallLocalId}-start start)`,
						insetInlineStart: `anchor(--button-${baseLocalId} end)`,
					}),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(isLeftPositionDynamic &&
						anchorLeft && {
							left: anchorLeft,
						}),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(isAnchoredToTargetEnd && {
						insetBlockStart: `calc(anchor(--card-${firstToolCallLocalId}-end start) + ${token(
							'space.300',
						)})`,
					}),
				}}
			>
				<SuggestionCard
					ref={cardRef}
					suggestion={selectedSuggestion}
					onNavigate={
						orderedSuggestionIds && orderedSuggestionIds.length > 1 ? onNavigate : undefined
					}
					onClose={onClose}
					onDiscard={onDiscard}
					onAccept={onAccept}
					stackSize={stackSize}
					onToggleOnlySuggestion={onToggleOnlySuggestion}
					isShowingOnlySuggestion={isShowingOnlySuggestion}
					onShowSidebar={onShowSidebar}
				/>
			</span>
		</>
	);
};
